import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useCoachTeams } from '../hooks/useCoachTeams';
import { useAuth } from './AuthContext';
import { coachStorageKey, COACH_STORAGE_SUFFIX } from '../utils/coachStorage';

type ActiveTeamSource = 'route' | 'storage' | 'auto';

export interface ActiveTeamState {
  activeTeamId: string | null;
  setActiveTeamId: (teamId: string | null, source?: ActiveTeamSource) => void;
  source: ActiveTeamSource | null;
}

const ActiveTeamContext = createContext<ActiveTeamState | null>(null);

export const ActiveTeamProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const { data: coachTeams = [] } = useCoachTeams();
  const { user } = useAuth();
  const userId = user?.id;

  const readStoredActiveTeamId = useCallback((): string | null => {
    if (!userId || typeof window === 'undefined') return null;
    try {
      return sessionStorage.getItem(coachStorageKey(userId, COACH_STORAGE_SUFFIX.activeTeamId));
    } catch {
      return null;
    }
  }, [userId]);

  const writeStoredActiveTeamId = useCallback(
    (teamId: string | null) => {
      if (!userId || typeof window === 'undefined') return;
      try {
        const key = coachStorageKey(userId, COACH_STORAGE_SUFFIX.activeTeamId);
        if (!teamId) sessionStorage.removeItem(key);
        else sessionStorage.setItem(key, teamId);
      } catch {
        /* non-fatal */
      }
    },
    [userId]
  );

  const [activeTeamId, _setActiveTeamId] = useState<string | null>(null);
  const [source, setSource] = useState<ActiveTeamSource | null>(null);

  useEffect(() => {
    if (!userId) {
      _setActiveTeamId(null);
      setSource(null);
      return;
    }
    const stored = readStoredActiveTeamId();
    _setActiveTeamId(stored);
    setSource(stored ? 'storage' : null);
  }, [userId, readStoredActiveTeamId]);

  const setActiveTeamId = useCallback(
    (teamId: string | null, s: ActiveTeamSource = 'storage') => {
      _setActiveTeamId(teamId);
      setSource(teamId ? s : null);
      writeStoredActiveTeamId(teamId);
    },
    [writeStoredActiveTeamId]
  );

  // Route-driven: when viewing a team detail page, that team becomes active.
  useEffect(() => {
    const match = location.pathname.match(/^\/coach\/teams\/([^/]+)$/);
    const teamIdFromRoute = match?.[1] ?? null;
    if (teamIdFromRoute && teamIdFromRoute !== activeTeamId) {
      setActiveTeamId(teamIdFromRoute, 'route');
    }
  }, [location.pathname, activeTeamId, setActiveTeamId]);

  // Validate stored/active id exists in current memberships (ignore synthetic demo ids).
  const validTeamIds = useMemo(() => {
    return new Set(coachTeams.map((m) => m.team_id).filter(Boolean));
  }, [coachTeams]);

  useEffect(() => {
    if (!activeTeamId) return;
    if (!validTeamIds.has(activeTeamId)) {
      // Team may have been deleted or membership revoked; clear.
      setActiveTeamId(null, 'storage');
    }
  }, [activeTeamId, validTeamIds, setActiveTeamId]);

  // Deterministic auto-selection: if the coach has exactly one qualifying team, make it active.
  useEffect(() => {
    if (activeTeamId) return;
    const nonDemo = coachTeams.filter((m) => m.team?.is_demo !== true);
    if (nonDemo.length === 1) {
      setActiveTeamId(nonDemo[0].team_id, 'auto');
    }
  }, [activeTeamId, coachTeams, setActiveTeamId]);

  const value = useMemo<ActiveTeamState>(
    () => ({ activeTeamId, setActiveTeamId, source }),
    [activeTeamId, setActiveTeamId, source]
  );

  return <ActiveTeamContext.Provider value={value}>{children}</ActiveTeamContext.Provider>;
};

export function useActiveTeam(): ActiveTeamState {
  const ctx = useContext(ActiveTeamContext);
  if (!ctx) {
    throw new Error('useActiveTeam must be used within ActiveTeamProvider');
  }
  return ctx;
}

/** Safe variant — returns null values instead of throwing when used outside the provider. */
export function useActiveTeamSafe(): ActiveTeamState {
  const ctx = useContext(ActiveTeamContext);
  return ctx ?? { activeTeamId: null, setActiveTeamId: () => {}, source: null };
}

export function useActiveTeamId(): string | null {
  return useActiveTeam().activeTeamId;
}
